import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Mail, Mail as MailIcon, Phone, Linkedin, Copy, ExternalLink, Building2,
  ShieldCheck, ShieldAlert, Sparkles, Save, Loader2, X,
  Activity, Eye, MousePointerClick, Reply, AlertTriangle,
  Briefcase, MessageSquare, Send, Globe2,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import EngagementScoreBadge from "@/components/EngagementScoreBadge";
import VerifyEntityDialog from "@/components/VerifyEntityDialog";
import ContactEmailHistory from "@/components/ContactEmailHistory";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function initials(c) {
  const a = (c?.first_name || "").trim()[0] || "";
  const b = (c?.last_name || "").trim()[0] || "";
  return (a + b).toUpperCase() || "?";
}

function CopyBtn({ value, label }) {
  return (
    <Button
      variant="ghost" size="sm" className="h-7 w-7 p-0"
      onClick={() => {
        navigator.clipboard.writeText(value);
        toast.success(`${label} copied`);
      }}
      title={`Copy ${label}`}
      data-testid={`copy-${label.toLowerCase()}`}
    >
      <Copy className="h-3.5 w-3.5" />
    </Button>
  );
}

function StatCard({ icon: Icon, label, value, tone = "default" }) {
  const tones = {
    default: "text-foreground",
    success: "text-emerald-600 dark:text-emerald-400",
    primary: "text-primary",
    warning: "text-amber-600 dark:text-amber-400",
    danger: "text-destructive",
  };
  return (
    <div className="rounded-md border border-border/60 p-2.5 flex items-center gap-2">
      <Icon className={`h-4 w-4 ${tones[tone] || tones.default}`} />
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-wide text-muted-foreground leading-none">{label}</div>
        <div className={`text-sm font-semibold leading-tight ${tones[tone] || tones.default}`}>{value}</div>
      </div>
    </div>
  );
}

function EmailRow({ e, t }) {
  const opened = (e.open_count || 0) > 0;
  const clicked = (e.click_count || 0) > 0;
  const status = e.bounced
    ? { label: t("contacts.bounced") || "Bounced", tone: "border-destructive/40 text-destructive" }
    : e.replied
      ? { label: t("contacts.replied") || "Replied", tone: "border-primary/40 text-primary" }
      : clicked
        ? { label: t("contacts.clicked") || "Clicked", tone: "border-emerald-400/60 text-emerald-600 dark:text-emerald-400" }
        : opened
          ? { label: t("contacts.opened") || "Opened", tone: "border-emerald-400/40 text-emerald-600/80 dark:text-emerald-400/80" }
          : { label: e.status || "sent", tone: "border-border text-muted-foreground" };
  return (
    <div className="flex items-start gap-2 rounded-md border border-border/60 px-3 py-2">
      <Send className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium truncate">{e.subject || "—"}</div>
          <Badge variant="outline" className={`text-[9px] ${status.tone}`}>{status.label}</Badge>
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
          <span>{fmtDate(e.sent_at || e.created_at)}</span>
          {opened && (
            <span className="inline-flex items-center gap-0.5">
              <Eye className="h-3 w-3" /> {e.open_count}
              {e.last_opened_at && <span className="text-muted-foreground/70"> · {fmtRelative(e.last_opened_at)}</span>}
            </span>
          )}
          {clicked && (
            <span className="inline-flex items-center gap-0.5">
              <MousePointerClick className="h-3 w-3" /> {e.click_count}
              {e.last_clicked_at && <span className="text-muted-foreground/70"> · {fmtRelative(e.last_clicked_at)}</span>}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function DealRow({ d, navigate, onClose, t }) {
  const stageBadge = (stage) => {
    const colors = {
      WON: "border-emerald-400/60 text-emerald-600 dark:text-emerald-400",
      LOST: "border-destructive/40 text-destructive",
      NEGOTIATION: "border-primary/40 text-primary",
      PROPOSAL_SENT: "border-blue-400/40 text-blue-600 dark:text-blue-400",
    };
    return colors[stage] || "border-border text-muted-foreground";
  };
  return (
    <div
      className="flex items-start gap-2 rounded-md border border-border/60 px-3 py-2 cursor-pointer hover:bg-muted/40"
      onClick={() => { onClose(); navigate(`/pipeline?deal=${d.id}`); }}
    >
      <Briefcase className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium truncate">{d.title || "—"}</div>
          <Badge variant="outline" className={`text-[9px] ${stageBadge(d.stage)}`}>
            {t(`pipeline.stages.${d.stage}`) || d.stage}
          </Badge>
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
          {d.company_name && <span>{d.company_name}</span>}
          {d.value > 0 && <span>· {(d.value || 0).toLocaleString()} {d.currency || ""}</span>}
          <span>· {fmtRelative(d.updated_at || d.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

function ActivityRow({ a }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-border/60 px-3 py-2">
      <MessageSquare className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[9px]">{a.type || "note"}</Badge>
          <div className="text-sm truncate">{a.subject || a.body?.slice(0, 80) || "—"}</div>
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          {fmtRelative(a.created_at)} {a.outcome ? `· ${a.outcome}` : ""}
        </div>
      </div>
    </div>
  );
}

function EngagementPanel({ loading, data, navigate, onClose, t }) {
  if (loading) {
    return (
      <div className="py-10 flex items-center justify-center text-muted-foreground" data-testid="engagement-loading">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        {t("contacts.loading") || "Loading…"}
      </div>
    );
  }
  if (!data) return null;
  const s = data.summary || {};
  const noData = (s.emails_total || 0) + (s.deals_total || 0) + (s.activities_total || 0) === 0;
  return (
    <div className="space-y-3" data-testid="engagement-panel">
      {/* Score card */}
      {data.score && (
        <div className="rounded-md border border-border/60 p-3 flex items-center gap-3 bg-gradient-to-r from-muted/40 to-transparent" data-testid="engagement-score-card">
          <EngagementScoreBadge
            score={data.score.score || 0}
            tier={data.score.tier || "cold"}
            size="md"
            showLabel
          />
          <div className="flex-1 text-[11px] text-muted-foreground">
            <div className="font-medium text-foreground mb-0.5">
              {t("contacts.score_explain") || "Engagement Score (90-day window)"}
            </div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5">
              <span>{t("contacts.opens") || "Opens"}: <b className="text-foreground">{data.score.breakdown?.opens || 0}</b></span>
              <span>{t("contacts.clicks") || "Clicks"}: <b className="text-foreground">{data.score.breakdown?.clicks || 0}</b></span>
              <span>{t("contacts.replies") || "Replies"}: <b className="text-foreground">{data.score.breakdown?.replies || 0}</b></span>
              <span>{t("contacts.bounces") || "Bounces"}: <b className="text-foreground">{data.score.breakdown?.bounces || 0}</b></span>
              <span>{t("contacts.deals_30d") || "Deals 30d"}: <b className="text-foreground">{data.score.breakdown?.deals_30d || 0}</b></span>
              <span>{t("contacts.activities_30d") || "Activities 30d"}: <b className="text-foreground">{data.score.breakdown?.activities_30d || 0}</b></span>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatCard icon={Send} label={t("contacts.emails_sent") || "Sent"} value={s.emails_total || 0} />
        <StatCard icon={Eye} label={t("contacts.emails_opened_kpi") || "Opened"} value={s.emails_opened || 0} tone="success" />
        <StatCard icon={MousePointerClick} label={t("contacts.emails_clicked_kpi") || "Clicked"} value={s.emails_clicked || 0} tone="primary" />
        <StatCard icon={Reply} label={t("contacts.emails_replied_kpi") || "Replied"} value={s.emails_replied || 0} tone="primary" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <StatCard icon={AlertTriangle} label={t("contacts.emails_bounced_kpi") || "Bounced"} value={s.emails_bounced || 0} tone="danger" />
        <StatCard icon={Briefcase} label={t("contacts.deals") || "Deals"} value={s.deals_total || 0} />
        <StatCard
          icon={Activity}
          label={t("contacts.last_engagement") || "Last activity"}
          value={s.last_engagement_at ? fmtRelative(s.last_engagement_at) : "—"}
        />
      </div>

      {noData && (
        <div className="py-8 text-center text-xs text-muted-foreground border border-dashed border-border/60 rounded-md">
          {t("contacts.no_engagement") || "No engagement history yet for this contact."}
        </div>
      )}

      {(data.emails?.length || 0) > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
            {t("contacts.recent_emails") || "Recent emails"} · {data.emails.length}
          </div>
          <ScrollArea className="max-h-44 pr-2">
            <div className="space-y-1.5">
              {data.emails.slice(0, 20).map((e) => <EmailRow key={e.id} e={e} t={t} />)}
            </div>
          </ScrollArea>
        </div>
      )}

      {(data.deals?.length || 0) > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
            {t("contacts.linked_deals") || "Linked deals"} · {data.deals.length}
          </div>
          <div className="space-y-1.5">
            {data.deals.slice(0, 10).map((d) => (
              <DealRow key={d.id} d={d} navigate={navigate} onClose={onClose} t={t} />
            ))}
          </div>
        </div>
      )}

      {(data.activities?.length || 0) > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
            {t("contacts.recent_activities") || "Recent activities"} · {data.activities.length}
          </div>
          <div className="space-y-1.5">
            {data.activities.slice(0, 10).map((a) => <ActivityRow key={a.id} a={a} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ContactDetailDialog({ open, onOpenChange, contact, onUpdated }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("info");
  const [engagement, setEngagement] = useState(null);
  const [loadingEng, setLoadingEng] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);

  useEffect(() => {
    if (contact) {
      setNotes(contact.notes || "");
      setEngagement(null);
      setTab("info");
    }
  }, [contact]);

  useEffect(() => {
    if (!open || !contact || tab !== "engagement" || engagement) return;
    let cancelled = false;
    (async () => {
      setLoadingEng(true);
      try {
        const { data } = await api.get(`/contacts/${contact.id}/engagement`);
        if (!cancelled) setEngagement(data);
      } catch (e) {
        if (!cancelled) toast.error(apiError(e), { duration: 10000, closeButton: true });
      } finally {
        if (!cancelled) setLoadingEng(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, contact, tab, engagement]);

  if (!contact) return null;

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.put(`/contacts/${contact.id}`, { notes });
      toast.success(t("common.saved") || "Saved");
      onUpdated?.();
    } catch (e) {
      toast.error(apiError(e), { duration: 10000, closeButton: true });
    } finally {
      setSaving(false);
    }
  };

  const fullName = `${contact.first_name || ""} ${contact.last_name || ""}`.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="contact-detail-dialog">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold shrink-0">
              {initials(contact)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <DialogTitle className="text-lg leading-tight" data-testid="contact-detail-name">
                  {fullName || "—"}
                </DialogTitle>
                <EngagementScoreBadge
                  score={contact.engagement_score || 0}
                  tier={contact.engagement_tier || "cold"}
                  size="sm"
                  showLabel
                />
              </div>
              <div className="text-sm text-muted-foreground mt-0.5">
                {contact.position || "—"}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {contact.multinational_risk?.is_multi && (
                  <Badge
                    variant="outline"
                    className="text-[10px] border-amber-500/60 text-amber-600 dark:text-amber-400 cursor-pointer hover:bg-amber-500/10"
                    onClick={() => setVerifyOpen(true)}
                    title={`Email domain '${contact.multinational_risk.domain}' is shared across many global subsidiaries — verify the actual entity.`}
                    data-testid="contact-multinational-badge"
                  >
                    <Globe2 className="h-2.5 w-2.5 mr-0.5" />
                    {t("contacts.multinational_risk") || "Multinational domain – verify"}
                  </Badge>
                )}
                {contact.verification_status === "MANUAL_VERIFIED" && (
                  <Badge variant="outline" className="text-[10px] border-emerald-500/60 text-emerald-600 dark:text-emerald-400">
                    <ShieldCheck className="h-2.5 w-2.5 mr-0.5" />
                    {t("contacts.entity_verified") || "Entity verified"}
                  </Badge>
                )}
                {contact.is_decision_maker && (
                  <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">
                    {t("contacts.decision_maker")}
                  </Badge>
                )}
                {contact.seniority && (
                  <Badge variant="secondary" className="text-[10px]">{contact.seniority}</Badge>
                )}
                {contact.source === "ai_agent" && (
                  <Badge variant="outline" className="text-[10px]">
                    <Sparkles className="h-2.5 w-2.5 mr-0.5" /> AI
                  </Badge>
                )}
                {contact.source && contact.source !== "ai_agent" && (
                  <Badge variant="outline" className="text-[10px]">{contact.source}</Badge>
                )}
                {contact.email && (
                  contact.email_verified ? (
                    <Badge variant="outline" className="text-[10px] border-emerald-400/60 text-emerald-600 dark:text-emerald-400">
                      <ShieldCheck className="h-2.5 w-2.5 mr-0.5" />
                      {t("contacts.verified") || "Verified"}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] border-amber-400/60 text-amber-600 dark:text-amber-400">
                      <ShieldAlert className="h-2.5 w-2.5 mr-0.5" />
                      {t("contacts.unverified")}
                    </Badge>
                  )
                )}
              </div>
            </div>
          </div>
        </DialogHeader>

        <Separator />

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-3 w-full" data-testid="contact-tabs">
            <TabsTrigger value="info" data-testid="tab-info">
              {t("contacts.tab_info") || "Information"}
            </TabsTrigger>
            <TabsTrigger value="engagement" data-testid="tab-engagement">
              <Activity className="h-3.5 w-3.5 mr-1.5" />
              {t("contacts.tab_engagement") || "Engagement"}
            </TabsTrigger>
            <TabsTrigger value="emails" data-testid="tab-emails">
              <MailIcon className="h-3.5 w-3.5 mr-1.5" />
              Emails
            </TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="mt-3">
            <div className="space-y-3">
          {/* Company */}
          {contact.company && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-border/60 px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {t("contacts.col_company")}
                  </div>
                  <div className="text-sm font-medium truncate">{contact.company.name}</div>
                </div>
              </div>
              <Button
                variant="ghost" size="sm" className="h-7 text-xs"
                onClick={() => { onOpenChange(false); navigate(`/companies/${contact.company_id}`); }}
                data-testid="contact-open-company"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                {t("contacts.open_company") || "Open"}
              </Button>
            </div>
          )}

          {/* Email */}
          <div className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2">
            <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {t("contacts.email")}
              </div>
              {contact.email ? (
                <a
                  href={`mailto:${contact.email}`}
                  className="text-sm font-medium text-primary hover:underline truncate block"
                  data-testid="contact-email-link"
                >
                  {contact.email}
                </a>
              ) : (
                <div className="text-sm text-muted-foreground/60">—</div>
              )}
            </div>
            {contact.email && (
              <>
                <CopyBtn value={contact.email} label="Email" />
                <Button
                  variant="outline" size="sm" className="h-7 text-xs"
                  asChild
                  data-testid="contact-email-send"
                >
                  <a href={`mailto:${contact.email}`}>
                    <Mail className="h-3 w-3 mr-1" />
                    {t("contacts.send") || "Send"}
                  </a>
                </Button>
              </>
            )}
          </div>

          {/* Phone */}
          <div className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2">
            <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {t("contacts.phone")}
              </div>
              {contact.phone ? (
                <a
                  href={`tel:${contact.phone}`}
                  className="text-sm font-medium text-primary hover:underline truncate block"
                  data-testid="contact-phone-link"
                >
                  {contact.phone}
                </a>
              ) : (
                <div className="text-sm text-muted-foreground/60">—</div>
              )}
            </div>
            {contact.phone && (
              <>
                <CopyBtn value={contact.phone} label="Phone" />
                <Button
                  variant="outline" size="sm" className="h-7 text-xs"
                  asChild
                  data-testid="contact-phone-call"
                >
                  <a href={`tel:${contact.phone}`}>
                    <Phone className="h-3 w-3 mr-1" />
                    {t("contacts.call") || "Call"}
                  </a>
                </Button>
              </>
            )}
          </div>

          {/* LinkedIn */}
          <div className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-2">
            <Linkedin className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">LinkedIn</div>
              {contact.linkedin_url ? (
                <a
                  href={contact.linkedin_url} target="_blank" rel="noreferrer"
                  className="text-sm font-medium text-primary hover:underline truncate block"
                  data-testid="contact-linkedin-link"
                >
                  {contact.linkedin_url}
                </a>
              ) : (
                <div className="text-sm text-muted-foreground/60">—</div>
              )}
            </div>
            {contact.linkedin_url && (
              <Button
                variant="outline" size="sm" className="h-7 text-xs"
                asChild
                data-testid="contact-linkedin-open"
              >
                <a href={contact.linkedin_url} target="_blank" rel="noreferrer">
                  <ExternalLink className="h-3 w-3 mr-1" />
                  {t("contacts.open") || "Open"}
                </a>
              </Button>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-1.5">
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {t("contacts.notes") || "Notes"}
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={t("contacts.notes_placeholder") || "Internal notes about this contact…"}
              data-testid="contact-notes-input"
            />
          </div>
            </div>
          </TabsContent>

          <TabsContent value="engagement" className="mt-3">
            <EngagementPanel
              loading={loadingEng}
              data={engagement}
              navigate={navigate}
              onClose={() => onOpenChange(false)}
              t={t}
            />
          </TabsContent>

          <TabsContent value="emails" className="mt-3">
            <ContactEmailHistory contactId={contact?.id} />
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          {contact.multinational_risk?.is_multi && (
            <Button
              variant="outline"
              onClick={() => setVerifyOpen(true)}
              className="border-amber-500/60 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
              data-testid="contact-verify-entity-btn"
            >
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
              {t("contacts.verify_via_linkedin") || "Verify via LinkedIn"}
            </Button>
          )}
          <Button
            variant="outline" onClick={() => onOpenChange(false)}
            data-testid="contact-detail-close"
          >
            <X className="h-3.5 w-3.5 mr-1.5" />
            {t("contacts.close") || "Close"}
          </Button>
          <Button
            onClick={saveNotes} disabled={saving || notes === (contact.notes || "")}
            data-testid="contact-save-notes"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            {t("contacts.save_notes") || "Save notes"}
          </Button>
        </DialogFooter>
      </DialogContent>

      <VerifyEntityDialog
        open={verifyOpen}
        onOpenChange={setVerifyOpen}
        contact={contact}
        onVerified={() => {
          onUpdated?.();
          onOpenChange(false);
        }}
      />
    </Dialog>
  );
}
